import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers, faDumbbell, faCalendarWeek, faClipboardList,
  faBellSlash, faEnvelopeOpenText, faMagnifyingGlass,
  faCircle, faChevronRight, faRotateRight,
  faFire, faChartLine, faUserPlus, faCalendarXmark, faUserSlash, faLocationDot,
  faTrophy, faTriangleExclamation, faUserCheck, faBell,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import styles from "./TeacherDashboard.module.css";
import { buildImageSrc } from "./userAdmin/userAdminHelpers";
import { describeOrgBilling } from "../utils/orgBilling";
import TeacherAnalyticsModal from "./TeacherAnalyticsModal";
import InviteStudentModal from "./InviteStudentModal";
import SendPushModal from "./SendPushModal";
import ConfirmDialog from "./ConfirmDialog";

// ── helpers ──────────────────────────────────────────────────────────────────
function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
}

function daysAgoLabel(days) {
  if (days === null || days === undefined) return "Nunca treinou";
  if (days === 0) return "Treinou hoje";
  if (days === 1) return "Treinou ontem";
  return `Há ${days} dias`;
}

function statusColor(student) {
  const { days_inactive, adherence_week, has_plan } = student;
  if (!has_plan) return "warning";
  if (days_inactive === null || days_inactive > 10) return "danger";
  if (days_inactive > 5 || adherence_week < 30) return "warning";
  return "ok";
}

// Mesmo critério do alerta "Planos vencendo" do painel admin (app/api/admin_dashboard.py):
// inclui já vencidos e os que vencem nos próximos 7 dias.
function planExpiryInfo(student) {
  if (!student.plan_expires_at) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(student.plan_expires_at + "T00:00:00");
  const diffDays = Math.round((exp - today) / 86400000);
  if (diffDays > 7) return null;
  return { expired: diffDays < 0, days: diffDays };
}


// ── Scorecard ─────────────────────────────────────────────────────────────────
function Scorecard({ icon, label, value, sub, accent, onClick, title }) {
  return (
    <button
      className={`${styles.scorecard} ${accent ? styles[`accent_${accent}`] : ""}`}
      onClick={onClick}
      data-tooltip={title}
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      <div className={styles.scorecardIcon}>
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className={styles.scorecardBody}>
        <div className={styles.scorecardValue}>{value ?? "-"}</div>
        <div className={styles.scorecardLabel}>{label}</div>
        {sub && <div className={styles.scorecardSub}>{sub}</div>}
      </div>
    </button>
  );
}

// ── Student Card ──────────────────────────────────────────────────────────────
function StudentCard({ student, onClick, onNudge, apiUrl, showOwner, currentUserId, outOfOrder }) {
  const status = statusColor(student);
  const pct = Math.min(student.adherence_week, 100);
  const expInfo = planExpiryInfo(student);

  // Atalho "Cutucar" — achado P1 da auditoria mobile de 06/09/2026: o painel
  // diagnostica muito bem quem está sumido, mas toda ação exigia abrir o
  // perfil do aluno (abrir → achar a ação → agir → voltar, vezes N alunos).
  // Só aparece quando faz sentido: aluno COM plano que parou de treinar.
  // Quem está sem plano precisa de outra coisa (atribuir ficha), não de
  // cutucão — esse caso continua indo pro perfil.
  const canNudge = !!onNudge && student.has_plan &&
    (student.days_inactive === null || student.days_inactive > 5);

  // O card deixou de ser <button> pra poder conter outro botão dentro
  // (button dentro de button é HTML inválido e quebra o clique no Safari).
  return (
    <div
      className={styles.studentCard}
      role="button"
      tabIndex={0}
      onClick={() => onClick(student)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(student); }
      }}
    >
      <div className={styles.studentCardLeft}>
        {student.avatar_url ? (
          <img
            src={student.avatar_url.startsWith("http")
              ? student.avatar_url.replace(/=s\d+-c/, "=s40-c")  // reduz tamanho p/ evitar 429
              : `${apiUrl}/${student.avatar_url}`}
            alt={student.name}
            referrerPolicy="no-referrer"
            loading="lazy"
            className={`${styles.avatar} ${styles.avatarImg} ${styles[`avatarStatus_${status}`]}`}
          />
        ) : (
          <div className={`${styles.avatar} ${styles[`avatarStatus_${status}`]}`}>
            {initials(student.name)}
          </div>
        )}
        <div className={styles.studentInfo}>
          <div className={styles.studentName}>{student.name}</div>
          {/* Visão "Todos da academia": de quem é o aluno (rodízio) */}
          {showOwner && student.professor_id !== currentUserId && (
            <div className={styles.studentOwner}>
              {student.professor_name ? `Prof. ${student.professor_name}` : "Sem professor"}
            </div>
          )}
          <div className={`${styles.studentSub} ${styles[`sub_${status}`]}`}>
            <FontAwesomeIcon icon={faCircle} className={styles.statusDot} />
            {daysAgoLabel(student.days_inactive)}
            {student.pending_report && (
              <span className={styles.reportInline} title="Report não lido">
                <FontAwesomeIcon icon={faEnvelopeOpenText} className={styles.reportBadgeIcon} />
              </span>
            )}
          </div>
        </div>
      </div>
      <div className={styles.studentCardRight}>
        <div className={styles.adherenceWrap}>
          <div className={styles.adherenceLabel}>{pct}% últimos 7 dias</div>
          <div className={styles.adherenceBar}>
            <div
              className={`${styles.adherenceFill} ${styles[`fill_${status}`]}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        {student.streak > 0 && (
          <div className={styles.streakBadge} title={`Streak: ${student.streak} dia${student.streak !== 1 ? 's' : ''} consecutivos treinando`}>
            <FontAwesomeIcon icon={faFire} /> {student.streak}d
          </div>
        )}
        {student.distinct_locations_week > 1 && (
          <div className={styles.locationBadge} title={`Treinou em ${student.distinct_locations_week} locais diferentes essa semana`}>
            <FontAwesomeIcon icon={faLocationDot} /> {student.distinct_locations_week}
          </div>
        )}
        {!student.has_plan && (
          <div className={styles.noPlanBadge}>Sem plano</div>
        )}
        {outOfOrder && (
          <div
            className={styles.orderBadge}
            title={`Em ${outOfOrder.hits} das últimas ${outOfOrder.of} sessões o treino saiu fora da ordem prescrita. A ordem pode refletir aparelho ocupado.`}
          >
            Treina fora da ordem
          </div>
        )}
        {expInfo && (
          <div className={`${styles.noPlanBadge} ${!expInfo.expired ? styles.planExpBadgeAmber : ""}`}>
            {expInfo.expired
              ? `Venceu há ${Math.abs(expInfo.days)}d`
              : expInfo.days === 0 ? "Vence hoje" : `Vence em ${expInfo.days}d`}
          </div>
        )}
        {canNudge && (
          <button
            type="button"
            className={styles.nudgeBtn}
            title={`Enviar notificação para ${student.name}`}
            aria-label={`Enviar notificação para ${student.name}`}
            onClick={(e) => { e.stopPropagation(); onNudge(student); }}
          >
            <FontAwesomeIcon icon={faBell} />
          </button>
        )}
        <FontAwesomeIcon icon={faChevronRight} className={styles.chevron} />
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
// Só aparece pra professor de academia (user.organization_id). "org" =
// carteira compartilhada: qualquer professor da academia atende qualquer
// aluno dela (rodízio) — ver app/services/org_access.py.
const SCOPES = [
  { key: "mine", label: "Meus alunos",        icon: faUserCheck },
  { key: "org",  label: "Todos da academia",  icon: faUsers },
];

// Quantos cards a lista mostra de cada vez. Os dados vêm inteiros do backend
// (filtros, contagens dos chips e busca continuam exatos no cliente); só a
// renderização é paginada: com a carteira da academia, desenhar 1000+ cards
// de uma vez travava a tela.
const LIST_PAGE_SIZE = 50;

const FILTERS = [
  { key: "all",        label: "Todos" },
  { key: "today",      label: "Hoje" },
  { key: "week",       label: "Últimos 7 dias" },
  { key: "attention",  label: "Em Atenção" },
  { key: "noplan",     label: "Sem plano" },
  { key: "planexp",    label: "Plano vencendo" },
  { key: "inactive",   label: "Desativados" },
];

export default function TeacherDashboard({ apiUrl, user, hideStudents, hasProfessorBillingAccess, lastSubscription, onOpenOrgPayments }) {
  // Membro de academia: a cobrança é da academia (ver utils/orgBilling.js).
  const orgBilling = user?.org_role ? user?.org_billing : null;
  const orgBillingInfo = describeOrgBilling(orgBilling);
  const isOrgOwner = user?.org_role === "owner";
  const isOrgManager = isOrgOwner || user?.org_role === "manager";
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [outOfOrder, setOutOfOrder] = useState({}); // student_id -> {hits, of}; vem à parte pra não atrasar a lista
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // Mostrar apenas ativos por padrão
  const hasOrg = !!user?.organization_id;
  const [scope, setScope] = useState("mine");
  const [visibleCount, setVisibleCount] = useState(LIST_PAGE_SIZE);
  // Topo da lista (título, busca e filtros) gruda ao rolar. O deslocamento é
  // calculado: no desktop o cabeçalho do app também é sticky e o bloco gruda
  // logo abaixo dele; no celular o cabeçalho rola junto e o bloco vai pro topo.
  const [toolbarTop, setToolbarTop] = useState(0);
  useEffect(() => {
    const header = document.querySelector("[data-app-header]");
    if (!header) return undefined;
    const compute = () => {
      const cs = window.getComputedStyle(header);
      const pinned = cs.position === "sticky" || cs.position === "fixed";
      setToolbarTop(pinned ? (parseFloat(cs.top) || 0) + header.offsetHeight + 8 : 0);
    };
    compute();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(compute) : null;
    ro?.observe(header);
    window.addEventListener("resize", compute);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, []);

  // Grudado ou não: quando grudado, uma faixa por trás cobre do topo da tela
  // até a barra (senão os cards aparecem nas frestas em volta do cabeçalho).
  const toolbarRef = useRef(null);
  const [toolbarStuck, setToolbarStuck] = useState(false);
  useEffect(() => {
    let frame = 0;
    const check = () => {
      frame = 0;
      const el = toolbarRef.current;
      if (!el) return;
      setToolbarStuck(el.getBoundingClientRect().top <= toolbarTop + 0.5 && window.scrollY > 0);
    };
    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(check); };
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [toolbarTop]);
  // Troca de filtro, busca ou visão volta a lista pro começo.
  useEffect(() => { setVisibleCount(LIST_PAGE_SIZE); }, [filter, search, scope]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  // Aluno alvo do atalho "Cutucar" da lista (ver StudentCard.canNudge).
  const [nudgeTarget, setNudgeTarget] = useState(null);

  // Leads de desafios públicos (Fase 6.2 — 2026-08-26): alunos self-service
  // que participaram e pontuaram em algum desafio criado por este professor,
  // ainda sem professor vinculado. Ver docs/07-issues.md.
  const [challengeLeads, setChallengeLeads] = useState([]);
  const [claimTarget, setClaimTarget] = useState(null); // lead selecionado pro ConfirmDialog
  const [claimLoading, setClaimLoading] = useState(false);

  const handleReportRead = useCallback((studentId) => {
    setStudents(prev => prev.map(s =>
      s.id === studentId ? { ...s, pending_report: false } : s
    ));
    // Decrement pending_reports in stats
    setStats(prev => prev ? { ...prev, pending_reports: Math.max(0, (prev.pending_reports || 1) - 1) } : prev);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { scope: hasOrg ? scope : "mine" };
      const [statsRes, studentsRes] = await Promise.all([
        api.get("/teacher/stats", { params }),
        api.get("/teacher/students-overview", { params }),
      ]);
      setStats(statsRes.data);
      setStudents(studentsRes.data);
    } catch (e) {
      console.error("TeacherDashboard load error:", e);
    } finally {
      setLoading(false);
    }
  }, [hasOrg, scope]);

  const loadChallengeLeads = useCallback(async () => {
    try {
      const res = await api.get("/users/challenge-leads");
      setChallengeLeads(res.data || []);
    } catch (e) {
      console.error("TeacherDashboard loadChallengeLeads error:", e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Selo "Treina fora da ordem": pedido depois da lista e sem bloquear nada.
  // Se falhar ou a chave geral estiver desligada, a lista só fica sem o selo.
  useEffect(() => {
    let alive = true;
    setOutOfOrder({});
    api.get("/teacher/order-pattern", { params: { scope: hasOrg ? scope : "mine" } })
      .then((r) => {
        if (!alive || !r.data?.enabled) return;
        const map = {};
        (r.data.flagged || []).forEach((f) => { map[f.student_id] = f; });
        setOutOfOrder(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [hasOrg, scope]);
  useEffect(() => { loadChallengeLeads(); }, [loadChallengeLeads]);

  const handleConfirmClaim = useCallback(async () => {
    if (!claimTarget) return;
    setClaimLoading(true);
    try {
      await api.post(`/users/${claimTarget.id}/claim-from-challenge`);
      setChallengeLeads((prev) => prev.filter((l) => l.id !== claimTarget.id));
      setClaimTarget(null);
      load(); // recarrega a lista de alunos pra mostrar o novo membro da turma
    } catch (e) {
      console.error("claim-from-challenge error:", e);
      alert(e?.response?.data?.detail || "Não foi possível atribuir este aluno. Tente novamente.");
    } finally {
      setClaimLoading(false);
    }
  }, [claimTarget, load]);

  // Filtro + busca
  const filtered = students.filter((s) => {
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;

    if (filter === "today")      return s.days_inactive === 0;
    if (filter === "week")       return s.sessions_week > 0;
    if (filter === "attention")  return s.is_active && statusColor(s) === "warning";  // mesma regra da cor do ponto
    if (filter === "noplan")     return !s.has_plan && s.is_active;
    if (filter === "planexp")    return s.is_active && !!planExpiryInfo(s);
    if (filter === "inactive")   return !s.is_active;

    // Por padrão (filter === "all"): mostrar apenas alunos ATIVOS (is_active = true)
    return s.is_active;
  });

  // Fonte única de verdade para "Em Atenção": mesma regra usada na cor do
  // ponto (statusColor), no filtro da aba e no card de KPI — antes cada um
  // usava um critério diferente (ver docs/07-issues.md)
  const attentionCount = useMemo(
    () => students.filter((s) => s.is_active && statusColor(s) === "warning").length,
    [students]
  );

  // Alunos ativos com plano vencido ou vencendo nos próximos 7 dias — mesmo
  // critério do alerta "Planos vencendo" do painel admin.
  const planExpCount = useMemo(
    () => students.filter((s) => s.is_active && !!planExpiryInfo(s)).length,
    [students]
  );
  const planExpiredCount = useMemo(
    () => students.filter((s) => s.is_active && planExpiryInfo(s)?.expired).length,
    [students]
  );

  // Ordenar: inativos primeiro, depois por dias sem treinar
  const sorted = [...filtered].sort((a, b) => {
    const statusOrder = { danger: 0, warning: 1, ok: 2 };
    const sa = statusOrder[statusColor(a)] ?? 3;
    const sb = statusOrder[statusColor(b)] ?? 3;
    if (sa !== sb) return sa - sb;
    const da = a.days_inactive ?? 9999;
    const db_ = b.days_inactive ?? 9999;
    return db_ - da;  // mais inativo aparece primeiro dentro do grupo
  });


  if (loading && !stats) {
    return (
      <div className={styles.loadingWrap}>
        <FontAwesomeIcon icon={faRotateRight} spin />
        <span>Carregando painel...</span>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* ── Cobrança da academia (plano por academia): bloqueio e carência ── */}
      {orgBillingInfo && (!hasProfessorBillingAccess || (orgBilling.status === "carencia" && isOrgManager)) && (
        <div
          className={hasProfessorBillingAccess ? styles.graceBanner : styles.accessLimitedBanner}
          role={hasProfessorBillingAccess ? "status" : "alert"}
        >
          <div className={styles.bannerContent}>
            <div className={styles.bannerText}>
              <h3>{hasProfessorBillingAccess ? orgBillingInfo.title : "Acesso da academia suspenso"}</h3>
              <p>{orgBillingInfo.detail}</p>
              {isOrgOwner && onOpenOrgPayments ? (
                <button type="button" className={styles.bannerAction} onClick={onOpenOrgPayments}>
                  {orgBilling.paid_through ? "Regularizar assinatura" : "Assinar plano da academia"}
                </button>
              ) : (
                <p>Fale com o dono da academia para regularizar.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Banner de acesso limitado ── */}
      {!hasProfessorBillingAccess && !orgBillingInfo && (
        <div className={styles.accessLimitedBanner}>
          <div className={styles.bannerContent}>
            <div className={styles.bannerText}>
              <h3>Seu acesso está limitado</h3>
              <p>
                Você não possui uma assinatura/pagamento ativo. Para gerenciar seus alunos e acessar todos os recursos,
                é necessário{" "}
                <strong>assinar um dos planos disponíveis</strong>.
              </p>
              {lastSubscription && lastSubscription.status === "canceled" && (
                <div className={styles.lastSubscriptionInfo}>
                  <strong>Última assinatura:</strong>
                  <span>
                    Cancelada em{" "}
                    {lastSubscription.updated_at
                      ? new Date(lastSubscription.updated_at.replace(' ', 'T')).toLocaleDateString("pt-BR")
                      : "-"}
                    {lastSubscription.current_period_end && (
                      <> (acesso válido até{" "}
                      {new Date(lastSubscription.current_period_end.replace(' ', 'T')).toLocaleDateString("pt-BR")})
                      </>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {hasOrg && (
        <div className={styles.scopeToggle} role="group" aria-label="Quais alunos mostrar" aria-busy={loading}>
          {SCOPES.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`${styles.chip} ${scope === s.key ? styles.chipActive : ""}`}
              aria-pressed={scope === s.key}
              onClick={() => setScope(s.key)}
            >
              <FontAwesomeIcon icon={s.icon} /> {s.label}
            </button>
          ))}
        </div>
      )}

      {/* ── KPI Cards Unificados (6 cards) ── */}
      <div className={styles.scorecards}>
        <Scorecard
          icon={faUsers}
          label="Total de Alunos"
          value={stats?.total_students}
          sub="cadastrados"
          title="Total de alunos cadastrados na sua turma"
          onClick={() => setFilter("all")}
        />
        <Scorecard
          icon={faDumbbell}
          label="Treinaram Hoje"
          value={stats?.trained_today}
          sub={stats ? `de ${stats.total_students}` : ""}
          accent="green"
          title="Alunos que registraram pelo menos 1 treino hoje"
          onClick={() => setFilter("today")}
        />
        <Scorecard
          icon={faCalendarWeek}
          label="Últimos 7 dias"
          value={stats && (stats.total_all || stats.total_students) ? `${Math.round((stats.trained_week / (stats.total_all || stats.total_students)) * 100)}%` : "0%"}
          sub={stats ? `${stats.trained_week} treinaram` : ""}
          accent="blue"
          title="% de alunos que treinaram pelo menos 1 vez nos últimos 7 dias"
          onClick={() => setFilter("week")}
        />
        <Scorecard
          icon={faChartLine}
          label="Aderência Média"
          value={stats != null ? `${stats.avg_adherence ?? 0}%` : "0%"}
          sub="da turma nos últimos 7 dias"
          title="Aderência média: sessões realizadas ÷ (meta semanal × semanas). Calculado nos últimos 7 dias."
          accent={
            stats == null ? undefined
            : stats.avg_adherence >= 60 ? "green"
            : stats.avg_adherence >= 30 ? "amber"
            : "red"
          }
        />
        <Scorecard
          icon={faBellSlash}
          label="Em Atenção"
          value={stats && (stats.total_all || stats.total_students) ? `${Math.round((attentionCount / (stats.total_all || stats.total_students)) * 100)}%` : "0%"}
          sub={stats ? `${attentionCount} alunos` : ""}
          accent={attentionCount > 0 ? "red" : undefined}
          title="Alunos ativos com mais de 5 dias sem treinar, aderência abaixo de 30% ou sem plano ativo"
          onClick={() => setFilter("attention")}
        />
        <Scorecard
          icon={faClipboardList}
          label="Sem Plano"
          value={stats && (stats.total_all || stats.total_students) ? `${Math.round((stats.no_plan / (stats.total_all || stats.total_students)) * 100)}%` : "0%"}
          sub={stats ? `${stats.no_plan} alunos` : "0"}
          accent="amber"
          title="Alunos cadastrados que ainda não têm um plano de treino ativo"
          onClick={() => setFilter("noplan")}
        />
        <Scorecard
          icon={faCalendarXmark}
          label="Plano Vencendo"
          value={planExpCount}
          sub={planExpiredCount > 0 ? `${planExpiredCount} já vencido${planExpiredCount > 1 ? "s" : ""}` : "próx. 7 dias"}
          accent={planExpiredCount > 0 ? "red" : planExpCount > 0 ? "amber" : undefined}
          title="Alunos com plano vencido ou que vence nos próximos 7 dias"
          onClick={() => setFilter("planexp")}
        />
        <Scorecard
          icon={faUserSlash}
          label="Desativados"
          value={stats?.inactive_count || 0}
          sub="alunos"
          title="Alunos com cadastro desativado"
          onClick={() => setFilter("inactive")}
        />
      </div>

      {/* ── Leads de desafios públicos (Fase 6.2) ── */}
      {!hideStudents && challengeLeads.length > 0 && (
        <div className={styles.listSection}>
          <div className={styles.listHeader}>
            <h3 className={styles.listTitle}>
              <FontAwesomeIcon icon={faTrophy} /> Leads de desafios
              <span className={styles.listCount}>{challengeLeads.length}</span>
            </h3>
          </div>
          <p style={{ margin: "0 0 12px", fontSize: "var(--fs-xs, 12px)", color: "var(--text-0-60)" }}>
            Alunos que participaram e pontuaram em algum desafio seu, mas ainda não têm professor vinculado.
          </p>
          <div className={styles.studentList}>
            {challengeLeads.map((lead) => (
              <div key={lead.id} className={styles.studentCard} style={{ cursor: "default" }}>
                <div className={styles.studentCardLeft}>
                  {lead.avatar_url ? (
                    <img
                      src={lead.avatar_url.startsWith("http")
                        ? lead.avatar_url.replace(/=s\d+-c/, "=s40-c")
                        : `${apiUrl}/${lead.avatar_url}`}
                      alt={lead.name}
                      referrerPolicy="no-referrer"
                      loading="lazy"
                      className={`${styles.avatar} ${styles.avatarImg}`}
                    />
                  ) : (
                    <div className={styles.avatar}>{initials(lead.name)}</div>
                  )}
                  <div className={styles.studentInfo}>
                    <div className={styles.studentName}>{lead.name}</div>
                    <div className={styles.studentSub}>
                      <FontAwesomeIcon icon={faTrophy} style={{ fontSize: "var(--fs-xs)" }} />
                      {lead.challenges.map((c) => c.title).join(", ")} · {lead.total_points} pts
                    </div>
                    {lead.has_paid_access && (
                      <div className={styles.studentSub} style={{ color: "var(--amber)" }}>
                        <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: "var(--fs-xs)" }} />
                        Já paga o TreinoNexo diretamente
                      </div>
                    )}
                  </div>
                </div>
                <button
                  className={styles.inviteBtn}
                  onClick={() => setClaimTarget(lead)}
                  title="Atribuir aluno à sua turma"
                >
                  <FontAwesomeIcon icon={faUserCheck} /> Atribuir
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Lista de alunos ── */}
      {!hideStudents && <div className={styles.listSection}>
        <div
          ref={toolbarRef}
          className={`${styles.listToolbar} ${toolbarStuck ? styles.listToolbarStuck : ""}`}
          style={{ top: toolbarTop, "--toolbar-cover": `${toolbarTop}px` }}
        >
        <div className={styles.listHeader}>
          <h3 className={styles.listTitle}>
            <FontAwesomeIcon icon={faUsers} /> Alunos
            <span className={styles.listCount}>{sorted.length}</span>
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {stats?.pending_reports > 0 && (
              <div className={styles.reportNotification}>
                <FontAwesomeIcon icon={faEnvelopeOpenText} className={styles.reportNotifIcon} />
                <span>{stats.pending_reports} report{stats.pending_reports > 1 ? 's' : ''} não lido{stats.pending_reports > 1 ? 's' : ''}</span>
              </div>
            )}
            <button
              className={styles.inviteBtn}
              onClick={() => setShowInvite(true)}
              title="Adicionar aluno (um por vez ou em massa)"
              style={user?.own_brand_color ? {
                background: user.own_brand_color + "22",
                borderColor: user.own_brand_color + "55",
                color: user.own_brand_color,
              } : undefined}
            >
              <FontAwesomeIcon icon={faUserPlus} /> Adicionar
            </button>
            <button className={styles.refreshBtn} onClick={load} title="Atualizar" aria-label="Atualizar lista de alunos">
              <FontAwesomeIcon icon={faRotateRight} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className={styles.searchWrap}>
          <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Buscar aluno..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Filter chips */}
        <div className={styles.filterChips}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`${styles.chip} ${filter === f.key ? styles.chipActive : ""}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              {f.key === "today" && stats?.trained_today > 0 && (
                <span className={styles.chipBadge}>{stats.trained_today}</span>
              )}
              {f.key === "week" && stats?.trained_week > 0 && (
                <span className={styles.chipBadge}>{stats.trained_week}</span>
              )}
              {f.key === "attention" && attentionCount > 0 && (
                <span className={styles.chipBadge}>{attentionCount}</span>
              )}
              {f.key === "noplan" && stats?.no_plan > 0 && (
                <span className={styles.chipBadge}>{stats.no_plan}</span>
              )}
              {f.key === "planexp" && planExpCount > 0 && (
                <span className={styles.chipBadge}>{planExpCount}</span>
              )}
              {f.key === "inactive" && stats?.inactive_count > 0 && (
                <span className={styles.chipBadge}>{stats.inactive_count}</span>
              )}
            </button>
          ))}
        </div>
        </div>

        {/* Legenda de cores */}
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendOk}`} /> Ativo
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendWarning}`} /> Atenção (&gt;5d, ader. &lt;30% ou sem plano)
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendDanger}`} /> Inativo (&gt;10d sem treinar)
          </span>
          <span className={styles.legendItem}>
            <FontAwesomeIcon icon={faFire} style={{color:'var(--amber)',fontSize: "var(--fs-xs)"}} /> dias seguidos
          </span>
        </div>

        {/* Student list */}
        {sorted.length === 0 ? (
          <div className={styles.emptyState}>
            <FontAwesomeIcon icon={faBellSlash} />
            <p>Nenhum aluno encontrado.</p>
          </div>
        ) : (
          <div className={styles.studentList}>
            {sorted.slice(0, visibleCount).map((s) => (
              <StudentCard
                key={s.id}
                student={s}
                onClick={setSelectedStudent}
                onNudge={setNudgeTarget}
                apiUrl={apiUrl}
                showOwner={hasOrg && scope === "org"}
                currentUserId={user?.id}
                outOfOrder={outOfOrder[s.id]}
              />
            ))}
            {sorted.length > visibleCount && (
              <button
                type="button"
                className={styles.loadMoreBtn}
                onClick={() => setVisibleCount((v) => v + LIST_PAGE_SIZE)}
              >
                Mostrar mais ({sorted.length - visibleCount} restantes)
              </button>
            )}
          </div>
        )}
      </div>
      }

      {/* ── Modal de convite ── */}
      <InviteStudentModal
        open={showInvite}
        onClose={() => setShowInvite(false)}
        professorLogoUrl={
          user?.custom_logo_url
            ? buildImageSrc(user.custom_logo_url, apiUrl)
            : null
        }
        professorBrandColor={user?.own_brand_color || null}
        professorName={user?.name || ""}
      />

      {/* ── Confirmação de atribuição de lead de desafio ── */}
      <ConfirmDialog
        open={!!claimTarget}
        title="Atribuir aluno à sua turma?"
        icon={faUserCheck}
        confirmLabel="Atribuir"
        confirmVariant="success"
        confirmIcon={faUserCheck}
        loading={claimLoading}
        onClose={() => (claimLoading ? null : setClaimTarget(null))}
        onConfirm={handleConfirmClaim}
        message={
          claimTarget && (
            <div style={{ textAlign: "left" }}>
              <p style={{ margin: "0 0 10px" }}>
                <strong>{claimTarget.name}</strong> participou e pontuou em{" "}
                {claimTarget.challenges.map((c) => c.title).join(", ")} ({claimTarget.total_points} pts) e vai passar
                a fazer parte da sua turma.
              </p>
              {claimTarget.has_paid_access && (
                <p style={{ margin: 0, color: "var(--amber)" }}>
                  <FontAwesomeIcon icon={faTriangleExclamation} /> Este aluno já paga o TreinoNexo diretamente. A
                  assinatura dele não será alterada automaticamente - combine o pagamento diretamente com ele depois
                  que o período atual terminar (ele pode cancelar quando quiser, mas o acesso pago continua valendo
                  até o fim do ciclo já pago).
                </p>
              )}
            </div>
          )
        }
      />

      {/* ── Modal do aluno ── */}
      {/* Cutucão rápido a partir da lista — mensagem padrão muda conforme o
          motivo do risco, e continua editável antes de enviar. */}
      <SendPushModal
        open={!!nudgeTarget}
        onClose={() => setNudgeTarget(null)}
        studentId={nudgeTarget?.id}
        studentName={nudgeTarget?.name}
        defaultMessage={
          nudgeTarget
            ? (nudgeTarget.days_inactive === null
                ? `Oi, ${String(nudgeTarget.name || "").split(" ")[0]}! Vi que você ainda não registrou nenhum treino por aqui. Precisa de ajuda pra começar?`
                : `Oi, ${String(nudgeTarget.name || "").split(" ")[0]}! Faz ${nudgeTarget.days_inactive} dias desde o seu último treino. Bora retomar?`)
            : ""
        }
      />

      {selectedStudent && (
        <TeacherAnalyticsModal
          open={true}
          inline={false}
          apiUrl={apiUrl}
          initialStudentId={selectedStudent.id}
          onClose={() => setSelectedStudent(null)}
          onReportRead={handleReportRead}
        />
      )}
    </div>
  );
}